// System
import axios from 'axios';
import { BaseSyntheticEvent, useMemo } from 'react';
import { useSnackbar } from 'notistack';
// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  setColumnVisibility,
  setCurrentLocalizations,
  setShowCreateLanguageDialog,
  setLocalizationTableFilters,
  setShowCreateLocalizationKeyDialog,
} from '@Modal/reducers/localizations/localizationSlice';
// Graphql
import {
  useExportLocalizationsLazyQuery,
  useUpdateLocalizationMutation,
} from '@Src/generated.graphql';
import { useLocalizationsQuery } from '@Src/generated.graphql';
import { useAddLocalizationKeyMutation } from '@Src/generated.graphql';
import { useAddNewLanguageMutation } from '@Src/generated.graphql';

// Types
import { StoreInterface } from 'src/app/types';
import { useCreateLocalizationKeyForm } from '@Forms/localizations/CreateLocalizationKeyFormContext';
import { useFormState } from 'react-hook-form';
import { UpdateLocalizationType } from '@Modal/reducers/localizations/types';
import Cookies from 'js-cookie';

export const useLocalization = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [exportLocalizations] = useExportLocalizationsLazyQuery();
  const [createLocalizationKeyMutation] = useAddLocalizationKeyMutation();
  const [updateLocalization] = useUpdateLocalizationMutation();
  const [addNewLanguage] = useAddNewLanguageMutation();

  const {
    selectedFilters,
    columnVisibility,
    currentLocalizations,
    columnVisibilityOptions,
    showCreateLanguageDialog,
    localizationTableFilters,
    showCreateLocalizationKeyDialog,
  } = useSelector((state: StoreInterface) => state?.localization);

  const { refetch: refetchLocalizations } = useLocalizationsQuery({
    variables: { languages: currentLocalizations },
  });

  const localizationsListFilter = useMemo(() => {
    const columnVisibilityStrArr = Array.from(Object.values(columnVisibility)).map((filt) => filt);
    return columnVisibilityStrArr as string[];
  }, [columnVisibility]);

  const handleApplyColumnVisibility = (column: string[]) => {
    if (column.length === 0) {
      dispatch(setColumnVisibility([]));
      return;
    }

    const newVisibleColumns = [...localizationsListFilter, ...column];
    if (localizationsListFilter.filter((col) => column.includes(col)).length > 0) {
      const filteredNewVisibleColumns = localizationsListFilter.filter((col) => col !== column[0]);
      dispatch(setColumnVisibility(filteredNewVisibleColumns));
    } else {
      dispatch(setColumnVisibility(newVisibleColumns));
    }
  };

  const handleSetColumnVisibility = (columns: string[]) => {
    dispatch(setColumnVisibility(columns));
  };


  const handleShowCreateLanguageDialog = (arg: boolean) => {
    dispatch(setShowCreateLanguageDialog(arg));
  };

  const visiblityCustomizable = useMemo(
    () => ({
      value: columnVisibility,
      options: columnVisibilityOptions,
      apply: (value: string[]) => handleApplyColumnVisibility(value),
    }),
    [columnVisibility, columnVisibilityOptions]
  );

  const handleLocalizationChange = (localization: string[] | string) =>
    dispatch(setCurrentLocalizations(localization));

  const handleExportLocalizations = async () => {
    try {
      const { data } = await exportLocalizations({
        variables: { languages: currentLocalizations },
      });
      const downloadUrl = data?.exportLocalizations;
      if (downloadUrl) window.open(downloadUrl, '_blank');
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  const handleCreateNewLanguage = async (arg: string) => {
    try {
      await addNewLanguage({ variables: { newLanguage: arg } });
      enqueueSnackbar('Languages was successfully created', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      dispatch(setShowCreateLanguageDialog(false));
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  const uploadLocalizations = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = Cookies.get('accessToken');
    try {
      await axios.post(`${process.env.REACT_APP_UPLOADS_API_PROXY_URL}/uploads/localizations`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: token ? `Bearer ${token}` : '',
        },
      });
      refetchLocalizations();
      enqueueSnackbar('Languages were successfully uploaded', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  // Form Controls
  const { control, handleSubmit, reset } = useCreateLocalizationKeyForm();
  const { isDirty, errors } = useFormState({ control });

  const handleCreateLocalizationKeyDialog = (arg: boolean) => {
    dispatch(setShowCreateLocalizationKeyDialog(arg));
    reset();
  };

  const handleSubmitCreateLocalizationKey = useMemo(
    () => (e?: BaseSyntheticEvent) => {
      return handleSubmit(async (formData) => {
        try {
          await createLocalizationKeyMutation({
            variables: { input: formData },
          });
          enqueueSnackbar('Language key was successfully created', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          await refetchLocalizations();

          handleCreateLocalizationKeyDialog(false);
          reset();
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    []
  );

  const handleUpdateLocalization = async (formData: UpdateLocalizationType) => {
    try {
      // @ts-ignore
      await updateLocalization({ variables: { input: formData } });
      enqueueSnackbar('Language value was successfully updated', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });

      await refetchLocalizations();
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  return {
    // State
    submittable: isDirty && Object.values(errors).length == 0,
    selectedFilters,
    columnVisibility,
    currentLocalizations,
    visiblityCustomizable,
    columnVisibilityOptions,
    localizationsListFilter,
    showCreateLanguageDialog,
    localizationTableFilters,
    showCreateLocalizationKeyDialog,
    // Actions
    setCurrentLocalizations,
    setLocalizationTableFilters,
    setShowCreateLocalizationKeyDialog,
    // Handlers
    uploadLocalizations,
    handleCreateNewLanguage,
    handleLocalizationChange,
    handleUpdateLocalization,
    handleExportLocalizations,
    handleApplyColumnVisibility,
    handleSetColumnVisibility,
    handleShowCreateLanguageDialog,
    handleCreateLocalizationKeyDialog,
    handleSubmitCreateLocalizationKey,
  };
};
